import React, { useState, useEffect, useCallback } from 'react';
import { getAppendixImages, fetchAppendixImageBlob, getSubcardImages, fetchSubcardImageBlob } from '../utils/storage';
import LazyBlobImage from './LazyBlobImage';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import './ReferenceImagesModal.css';

// Optional sub-card scoping. When `subcardId` is set, the modal additionally
// surfaces the auto-generated images for that sub-card and onSave returns
// an object { referenceImageIds, subcardImageIds } instead of a flat array.
// Without `subcardId` the modal preserves its original synopsis behavior.
export default function ReferenceImagesModal({
  inspectionId,
  initialSelected,                  // legacy: array of appendix image UUIDs
  initialSelectedSubcardImages,     // optional: array of subcard image numeric IDs
  subcardId,                        // optional: when set, includes sub-card pool
  onSave,
  onCancel,
}) {
  const readOnly = useReadOnly();   // archived → view only (no select/deselect, no save)
  const [images, setImages] = useState([]);
  const [subcardImages, setSubcardImages] = useState([]);
  const [selected, setSelected] = useState(() => new Set(initialSelected || []));
  const [selectedSubcard, setSelectedSubcard] = useState(
    () => new Set(initialSelectedSubcardImages || []),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const tasks = [getAppendixImages(inspectionId)];
    if (subcardId) tasks.push(getSubcardImages(inspectionId, subcardId));
    Promise.all(tasks)
      .then(([appendix, generated]) => {
        setImages(appendix || []);
        // Drop stale appendix selections too — ids a record still references
        // although the image was deleted from the appendix. Without this the
        // Save button counts them while the grid shows nothing.
        const liveAppendix = new Set((appendix || []).map((a) => String(a.id)));
        setSelected((prev) => {
          const next = new Set([...prev].filter((id) => liveAppendix.has(String(id))));
          return next.size === prev.size ? prev : next;
        });
        if (generated) {
          setSubcardImages(generated);
          // Defensively drop stale selections — ids the sub-card record
          // still references but whose underlying image rows have been
          // purged by a more recent regen. Without this, the Save button
          // counts them even though they don't render in the grid.
          const liveIds = new Set(generated.map((g) => g.id));
          setSelectedSubcard((prev) => {
            let mutated = false;
            const next = new Set();
            prev.forEach((id) => {
              if (liveIds.has(id)) next.add(id);
              else mutated = true;
            });
            return mutated ? next : prev;
          });
        }
        setLoading(false);
      })
      .catch((err) => { console.error('Failed to load images:', err); setLoading(false); });
  }, [inspectionId, subcardId]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSubcard = useCallback((id) => {
    setSelectedSubcard((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    if (subcardId) {
      await onSave({
        referenceImageIds: Array.from(selected),
        subcardImageIds: Array.from(selectedSubcard),
      });
    } else {
      await onSave(Array.from(selected));
    }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal ref-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Reference Images</h2>
        <p className="modal-body-text">
          Select images from the appendix to reference in the list of annotations.
        </p>

        <div className="ref-modal-body">
          {loading ? (
            <div className="ref-modal-empty">Loading...</div>
          ) : (
            <>
              {subcardId && subcardImages.length > 0 && (
                <>
                  <div className="ref-modal-section-title">Generated for this sub-card</div>
                  <div className="cg-grid">
                    {subcardImages.map((img) => {
                      const isSelected = selectedSubcard.has(img.id);
                      return (
                        <div
                          key={`sc-${img.id}`}
                          className={`cg-card ref-modal-card${isSelected ? ' selected' : ''}`}
                          onClick={readOnly ? undefined : () => toggleSubcard(img.id)}
                        >
                          <div className="cg-image-wrapper">
                            <LazyBlobImage
                              fetchBlob={() => fetchSubcardImageBlob(img.id)}
                              alt={img.name}
                              className="cg-image"
                            />
                            {isSelected && (
                              <div className="ref-modal-check">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="cg-caption">
                            <span className="appendix-caption-text">{img.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {subcardId && <div className="ref-modal-section-title">From appendix</div>}
              {images.length === 0 ? (
                <div className="ref-modal-empty">No appendix images available.</div>
              ) : (
                <div className="cg-grid">
                  {images.map((img) => {
                    const isSelected = selected.has(img.id);
                    return (
                      <div
                        key={img.id}
                        className={`cg-card ref-modal-card${isSelected ? ' selected' : ''}`}
                        onClick={readOnly ? undefined : () => toggle(img.id)}
                      >
                        <div className="cg-image-wrapper">
                          <LazyBlobImage
                            fetchBlob={() => fetchAppendixImageBlob(img.id)}
                            alt={img.caption || 'Appendix image'}
                            className="cg-image"
                          />
                          {isSelected && (
                            <div className="ref-modal-check">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="cg-caption">
                          <span className="appendix-caption-text">{img.caption || 'Untitled'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          {readOnly ? (
            <button className="btn btn-secondary" onClick={onCancel}>Close</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
                {saving ? 'Saving...' : `Save (${selected.size + selectedSubcard.size} selected)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
